"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { useLanguage } from "@/lib/language-context"
import { api } from "@/lib/api"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Search, Package, ArrowRight } from "lucide-react"

interface Category {
  id: number
  name: string
  name_uz?: string
  name_en?: string
  name_ru?: string
  slug: string
  description?: string
  image?: string | null
  products_count?: number // Changed from product_count to match backend
}

export function CategoriesContent() {
  const { t, currentLang } = useLanguage()
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await api.getCategories()
        setCategories(data || [])
      } catch (error) {
        console.error("Error fetching categories:", error)
        setCategories([])
      } finally {
        setLoading(false)
      }
    }
    fetchCategories()
  }, [])

  const getCategoryName = (category: Category) => {
    if (currentLang === "uz" && category.name_uz) return category.name_uz
    if (currentLang === "en" && category.name_en) return category.name_en
    if (currentLang === "ru" && category.name_ru) return category.name_ru
    return category.name
  }

  const filteredCategories = categories.filter((category) =>
    getCategoryName(category).toLowerCase().includes(searchQuery.toLowerCase()),
  )

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="space-y-4 mb-12">
          <Skeleton className="h-12 w-64 rounded-xl" />
          <Skeleton className="h-6 w-full max-w-xl rounded-lg" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-72 rounded-2xl shadow-sm" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#fafafa] dark:bg-zinc-950">
      {/* Premium Header */}
      <div className="relative overflow-hidden bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800">
        <div className="absolute top-0 right-0 -translate-y-12 translate-x-12 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 translate-y-12 -translate-x-12 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />

        <div className="container mx-auto px-4 py-16 md:py-24 relative z-10">
          <div className="max-w-3xl">
            <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/20 bg-primary/5 text-primary animate-in fade-in slide-in-from-bottom-2">
              {t("categories", "explore_categories")}
            </Badge>
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-zinc-900 dark:text-zinc-50 mb-6 animate-in fade-in slide-in-from-bottom-3 duration-500">
              {t("nav", "categories")}
            </h1>
            <p className="text-lg md:text-xl text-zinc-500 dark:text-zinc-400 mb-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
              {t("home", "categories_subtitle")}
            </p>

            {/* Elegant Search */}
            <div className="relative max-w-lg animate-in fade-in slide-in-from-bottom-5 duration-1000">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
              <Input
                type="text"
                placeholder={t("header", "search_placeholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-14 bg-zinc-50 dark:bg-zinc-800/50 border-zinc-200 dark:border-zinc-700 rounded-2xl shadow-sm focus:ring-2 focus:ring-primary/20 transition-all text-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="container mx-auto px-4 py-16">
        {filteredCategories.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {filteredCategories.map((category, index) => (
              <Link
                key={category.id}
                to={`/products?category=${category.slug}`}
                className="group h-full animate-in fade-in zoom-in-95 duration-500"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <Card className="h-full border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden hover:shadow-2xl hover:shadow-primary/5 transition-all duration-500 hover:-translate-y-2">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    {category.image ? (
                      <img
                        src={category.image || "/placeholder.svg"}
                        alt={getCategoryName(category)}
                        className="object-cover w-full h-full group-hover:scale-110 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                        <Package className="h-16 w-16 text-zinc-300 dark:text-zinc-600 group-hover:text-primary/40 transition-colors" />
                      </div>
                    )}

                    {/* Overlay with Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />

                    {/* Badge on Image */}
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-white/90 dark:bg-zinc-900/90 text-zinc-900 dark:text-zinc-50 backdrop-blur-md border-none shadow-sm px-3 py-1 scale-100 group-hover:scale-110 transition-transform">
                        {category.products_count || 0} {t("categories", "products_count")}
                      </Badge>
                    </div>

                    {/* Quick Action */}
                    <div className="absolute bottom-4 left-4 right-4 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                      <div className="flex items-center text-white font-medium text-sm">
                        {t("categories", "view_products")}
                        <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>

                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-50 mb-2 group-hover:text-primary transition-colors">
                      {getCategoryName(category)}
                    </h3>
                    {category.description && (
                      <p className="text-sm text-zinc-500 dark:text-zinc-400 line-clamp-2 mb-4 leading-relaxed">
                        {category.description}
                      </p>
                    )}
                    <div className="aspect-square w-1 h-1 bg-zinc-200 dark:bg-zinc-800 rounded-full group-hover:w-full group-hover:bg-primary/30 transition-all duration-500" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-24 bg-zinc-50 dark:bg-zinc-900/50 rounded-3xl border-2 border-dashed border-zinc-200 dark:border-zinc-800 animate-in fade-in duration-700">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-zinc-100 dark:bg-zinc-800 mb-6">
              <Package className="h-10 w-10 text-zinc-400 dark:text-zinc-500" />
            </div>
            <h3 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50 mb-3">
              {t("categories", "no_categories") || t("home", "no_categories")}
            </h3>
            <p className="text-zinc-500 dark:text-zinc-400 max-w-sm mx-auto">
              {searchQuery ? t("home", "no_search_results") : t("home", "no_categories_desc")}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
